import { Route } from '@angular/router';
import { visitorGuard } from '@core/utils/guards/visitor.guard';
import { provideState } from '@ngrx/store';
import * as fromPublicProfileReducers from '@features/public-profile/data-access/reducers/public-profile.reducer';
import * as fromPublicEventReducers from '@features/public-event/data-access/reducers/public-event.reducer';
import * as fromEventInvitationReducers from '@features/event-invitation/data-access/reducers/event-invitation.reducer';
import * as fromPublicOfferFormReducers from '@features/public-offer-form/data-access/reducers/public-offer-form.reducer';
import * as fromPublicDemandFormReducers from '@features/public-demand-form/data-access/reducers/public-demand-form.reducer';
import * as fromPublicOfferInterestFormReducers from '@features/public-offer-interest-form/data-access/reducers/public-offer-interest-form.reducer';
import * as fromPublicDemandInterestFormReducers from '@features/public-demand-interest-form/data-access/reducers/public-demand-interest-form.reducer';
import { provideEffects } from '@ngrx/effects';
import { userGuard } from '@core/utils/guards/user.guard';
import { onboardingGuard } from '@features/onboarding/utils/guards/onboarding.guard';
import { PublicProfileService } from '@features/public-profile/data-access/services/public-profile.service';
import { PublicProfileEffects } from '@features/public-profile/data-access/effects/public-profile.effects';
import { adminGuard } from '@features/admin/utils/guards/admin.guard';
import { makeEnvironmentProviders } from '@angular/core';
import { publicProfileGuard } from '@features/public-profile/utils/guards/public-profile.guard';
import { UserType } from '@core/utils/models/user.model';
import { routeParamGuard } from '@core/utils/guards/route-param.guard';
import { PublicEventService } from '@features/public-event/data-access/services/public-event.service';
import { PublicEventEffects } from '@features/public-event/data-access/effects/public-event.effects';
import { EventInvitationService } from '@features/event-invitation/data-access/services/event-invitation.service';
import { EventInvitationEffects } from '@features/event-invitation/data-access/effects/event-invitation.effects';
import { PublicOfferFormService } from '@features/public-offer-form/data-access/services/public-offer-form.service';
import { PublicOfferFormEffects } from '@features/public-offer-form/data-access/effects/public-offer-form.effects';
import { PublicDemandFormService } from '@features/public-demand-form/data-access/services/public-demand-form.service';
import { PublicDemandFormEffects } from '@features/public-demand-form/data-access/effects/public-demand-form.effects';
import { PublicOfferInterestFormService } from '@features/public-offer-interest-form/data-access/services/public-offer-interest-form.service';
import { PublicOfferInterestFormEffects } from '@features/public-offer-interest-form/data-access/effects/public-offer-interest-form.effects';
import { PublicDemandInterestFormService } from '@features/public-demand-interest-form/data-access/services/public-demand-interest-form.service';
import { PublicDemandInterestFormEffects } from '@features/public-demand-interest-form/data-access/effects/public-demand-interest-form.effects';

export const appRoutes: Route[] = [
  {
    path: 'onboarding',
    title: 'تسجيل الدخول - لينك فال',
    canMatch: [userGuard(), onboardingGuard],
    loadChildren: () => import('@features/onboarding/feature/onboarding-shell/onboarding.routes'),
  },
  {
    path: 'admin',
    title: 'لوحة التحكم - لينك فال',
    canMatch: [userGuard(), adminGuard],
    loadChildren: () => import('@features/admin/feature/admin-shell/admin.routes'),
  },
  {
    path: 'backoffice',
    title: 'إدارة المنصة - لينك فال',
    canMatch: [userGuard(UserType.backoffice)],
    loadChildren: () => import('@features/backoffice/feature/backoffice-shell/backoffice.routes'),
  },
  {
    path: 'payment',
    title: 'الدفع - لينك فال',
    canMatch: [userGuard(), adminGuard],
    loadChildren: () => import('@features/payment/feature/payment-shell/payment.routes'),
  },
  {
    path: 'a/:hash',
    canActivate: [routeParamGuard('hash')],
    title: 'لينك فال',
    loadComponent: () => import('@features/affiliate/feature/affiliate.component'),
  },
  {
    path: 'i/:hash',
    canActivate: [routeParamGuard('hash')],
    loadComponent: () => import('@features/event-invitation/feature/event-invitation.component'),
    providers: [
      EventInvitationService,
      makeEnvironmentProviders([
        provideState(fromEventInvitationReducers.eventInvitationFeatureKey, fromEventInvitationReducers.reducer, {
          metaReducers: fromEventInvitationReducers.metaReducers,
        }),
      ]),
      provideEffects([EventInvitationEffects]),
    ],
  },
  {
    path: 'event/:hash',
    canActivate: [routeParamGuard('hash')],
    loadComponent: () => import('@features/public-event/feature/public-event.component'),
    providers: [
      PublicEventService,
      makeEnvironmentProviders([
        provideState(fromPublicEventReducers.publicEventFeatureKey, fromPublicEventReducers.reducer, {
          metaReducers: fromPublicEventReducers.metaReducers,
        }),
      ]),
      provideEffects([PublicEventEffects]),
    ],
  },
  {
    path: 'of/:userId',
    canActivate: [routeParamGuard('userId')],
    loadComponent: () => import('@features/public-offer-form/feature/public-offer-form.component'),
    providers: [
      PublicOfferFormService,
      makeEnvironmentProviders([
        provideState(fromPublicOfferFormReducers.publicOfferFormFeatureKey, fromPublicOfferFormReducers.reducer, {
          metaReducers: fromPublicOfferFormReducers.metaReducers,
        }),
      ]),
      provideEffects([PublicOfferFormEffects]),
    ],
  },
  {
    path: 'df/:userId',
    canActivate: [routeParamGuard('userId')],
    loadComponent: () => import('@features/public-demand-form/feature/public-demand-form.component'),
    providers: [
      PublicDemandFormService,
      makeEnvironmentProviders([
        provideState(fromPublicDemandFormReducers.publicDemandFormFeatureKey, fromPublicDemandFormReducers.reducer, {
          metaReducers: fromPublicDemandFormReducers.metaReducers,
        }),
      ]),
      provideEffects([PublicDemandFormEffects]),
    ],
  },
  {
    path: 'oif/:offerHash',
    canActivate: [routeParamGuard('offerHash')],
    loadComponent: () => import('@features/public-offer-interest-form/feature/public-offer-interest-form.component'),
    providers: [
      PublicOfferInterestFormService,
      makeEnvironmentProviders([
        provideState(
          fromPublicOfferInterestFormReducers.publicOfferInterestFormFeatureKey,
          fromPublicOfferInterestFormReducers.reducer,
          {
            metaReducers: fromPublicOfferInterestFormReducers.metaReducers,
          }
        ),
      ]),
      provideEffects([PublicOfferInterestFormEffects]),
    ],
  },
  {
    path: 'dif/:demandHash',
    canActivate: [routeParamGuard('demandHash')],
    loadComponent: () => import('@features/public-demand-interest-form/feature/public-demand-interest-form.component'),
    providers: [
      PublicDemandInterestFormService,
      makeEnvironmentProviders([
        provideState(
          fromPublicDemandInterestFormReducers.publicDemandInterestFormFeatureKey,
          fromPublicDemandInterestFormReducers.reducer,
          {
            metaReducers: fromPublicDemandInterestFormReducers.metaReducers,
          }
        ),
      ]),
      provideEffects([PublicDemandInterestFormEffects]),
    ],
  },
  {
    path: ':slug',
    canMatch: [publicProfileGuard],
    loadComponent: () => import('@features/public-profile/feature/public-profile.component'),
    providers: [
      PublicProfileService,
      makeEnvironmentProviders([
        provideState(fromPublicProfileReducers.publicProfileFeatureKey, fromPublicProfileReducers.reducer, {
          metaReducers: fromPublicProfileReducers.metaReducers,
        }),
      ]),
      provideEffects([PublicProfileEffects]),
    ],
  },
  {
    path: '',
    title: 'الصفحة الرئيسية - لينك فال',
    loadChildren: () => import('@features/main/feature/main-shell/main.routes'),
  },
  {
    path: '',
    title: 'تسجيل الدخول - لينك فال',
    canMatch: [visitorGuard],
    loadChildren: () => import('@features/auth/feature/auth-shell/auth.routes'),
  },
  {
    path: '**',
    redirectTo: '/login',
  },
];
